import {useContext, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import MoleculeAvatar from '@s-ui/react-molecule-avatar'
import FormComment, {FORM_COMMENT_SPACING} from '@adv-mt/mt-form-comment'
import IconComments from '@adv-mt/mt-svg-icons/lib/Comments'

import {BASE_CLASS, COMMENT_BASE_CLASS} from './config.js'
import {DatePublished} from './datePublished.js'

const ReplyUI = ({parentCommentId, publishCommentPartialProps}) => {
  const {articleId, user, onPublishSuccess} = publishCommentPartialProps

  const {i18n} = useContext(Context)

  const [showReplyForm, setShowReplyForm] = useState(false)
  const cancelCallback = () => setShowReplyForm(false)
  const onAfterPublishSuccess = formParams => {
    onPublishSuccess(formParams)
    setShowReplyForm(false)
  }

  const formReplyProps = {
    publish: {
      data: {
        articleId,
        parentCommentId
      },
      button: {
        color: atomButtonColors.PRIMARY,
        design: atomButtonDesigns.SOLID,
        showIcon: true
      },
      onAfterPublish: onAfterPublishSuccess
    },
    cancel: {
      button: {
        onClick: cancelCallback
      }
    },
    inputPlaceholder: i18n.t('COMMENTS.ADD_REPLY'),
    spacing: FORM_COMMENT_SPACING.NONE,
    shadow: false,
    showTitle: false,
    user
  }

  if (showReplyForm) return <FormComment {...formReplyProps} />

  return (
    <div className={`${BASE_CLASS}-reply`}>
      <AtomButton
        size={atomButtonSizes.SMALL}
        leftIcon={<IconComments />}
        color={atomButtonColors.PRIMARY}
        design={atomButtonDesigns.OUTLINE}
        shape={atomButtonShapes.CIRCULAR}
        onClick={() => setShowReplyForm(true)}
      >
        {i18n.t('COMMENTS.REPLY')}
      </AtomButton>
    </div>
  )
}

const CommentBlock = ({
  id,
  author,
  date,
  text,
  showThreadLine,
  numberOfAnswers,
  children,
  parentCommentId,
  publishCommentPartialProps,
  isNew,
  isReply
}) => {
  const {i18n} = useContext(Context)

  const textSeparator = ' | '
  const numberOfAnswersText = numberOfAnswers > 0 ? i18n.t('COMMENTS.ANSWERS_COUNT', numberOfAnswers) : ''
  const showReplyUI = Boolean(publishCommentPartialProps && publishCommentPartialProps.user)
  const rootClassNames = cx(COMMENT_BASE_CLASS, {
    [`${COMMENT_BASE_CLASS}--reply`]: isReply,
    [`${COMMENT_BASE_CLASS}--new`]: isNew
  })
  const recentlyAddedCommentRef = useRef()
  useMount(() => {
    if (isNew) {
      setTimeout(() => {
        recentlyAddedCommentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }, 500)
    }
  })

  const authorName = author.name || i18n.t('COMMENTS.DEFAULT_NAME')
  const authorCommentText = text || i18n.t('COMMENTS.DEFAULT_COMMENT')

  return (
    <div ref={recentlyAddedCommentRef} className={rootClassNames} key={id}>
      <div
        className={`${COMMENT_BASE_CLASS}AuthorPhoto ${
          showThreadLine ? `${COMMENT_BASE_CLASS}AuthorPhoto--thread` : ''
        }`}
      >
        <MoleculeAvatar name={author.name} src={author.photoUrl} />
      </div>

      <div className={`${COMMENT_BASE_CLASS}Info`}>
        <div className={`${COMMENT_BASE_CLASS}InfoHeader`}>
          <div className={`${COMMENT_BASE_CLASS}InfoHeaderAuthor`}>{authorName}</div>
          <DatePublished date={date} />
          {numberOfAnswersText && (
            <span data-testid={`${id}-answers`}>
              {textSeparator}
              {numberOfAnswersText}
            </span>
          )}
        </div>
        <p
          className={`${COMMENT_BASE_CLASS}InfoParagraph`}
          dangerouslySetInnerHTML={{
            __html: authorCommentText
          }}
        />
        {showReplyUI && (
          <ReplyUI publishCommentPartialProps={publishCommentPartialProps} parentCommentId={parentCommentId} />
        )}
        {children}
      </div>
    </div>
  )
}

export default function ListComments({comments, publishCommentPartialProps}) {
  return (
    <div className={BASE_CLASS}>
      {comments.map(({answers, ...commentProps}) => (
        <CommentBlock
          {...commentProps}
          numberOfAnswers={answers.length}
          key={commentProps.id}
          publishCommentPartialProps={publishCommentPartialProps}
          parentCommentId={commentProps.id}
        >
          {Boolean(answers.length) && (
            <div className={`${BASE_CLASS}-answers`}>
              {answers.map((answer, index) => {
                const isLastReply = index === answers.length - 1

                return <CommentBlock {...answer} key={answer.id} showThreadLine={!isLastReply} isReply />
              })}
            </div>
          )}
        </CommentBlock>
      ))}
    </div>
  )
}

const COMMENT_BASE_PROPS = {
  id: PropTypes.string.isRequired,
  author: PropTypes.shape({
    name: PropTypes.string,
    photoUrl: PropTypes.string
  }).isRequired,
  date: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  isNew: PropTypes.bool
}

const ANSWERS_PROPS = {
  ...COMMENT_BASE_PROPS,
  showThreadLine: PropTypes.bool,
  numberOfAnswers: PropTypes.number,
  children: PropTypes.element,
  parentCommentId: PropTypes.string,
  isReply: PropTypes.bool
}

const MAIN_COMMENT_PROPS = {
  ...COMMENT_BASE_PROPS,
  answers: PropTypes.arrayOf(PropTypes.shape(ANSWERS_PROPS))
}

const PUBLISH_COMMENT_PARTIAL_PROPS = PropTypes.shape({
  articleId: PropTypes.string,
  onPublishSuccess: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    photoUrl: PropTypes.string
  })
})

const COMMENTS_LIST_PROPS = {
  publishCommentPartialProps: PUBLISH_COMMENT_PARTIAL_PROPS,
  comments: PropTypes.arrayOf(PropTypes.shape(MAIN_COMMENT_PROPS)).isRequired
}

ListComments.displayName = 'ListComments'
ListComments.propTypes = COMMENTS_LIST_PROPS
CommentBlock.propTypes = ANSWERS_PROPS
ReplyUI.propTypes = {
  parentCommentId: PropTypes.string,
  publishCommentPartialProps: PUBLISH_COMMENT_PARTIAL_PROPS
}
